import TranslationService from "../../services/TranslationService";
import Vue from "vue";
import { mapState } from "vuex";
import { isDefined } from "../../helper/utils";

const NotificationService = require("../../services/NotificationService");

function collectPossibleValues(method) {
  const values = [];

  const pushValue = (value) => {
    if (typeof value === "string" && value.trim().length) {
      values.push(value.trim().toLowerCase());
    }
  };

  if (!method || typeof method !== "object") {
    return values;
  }

  pushValue(method.name);
  pushValue(method.backendName);
  pushValue(method.frontendName);
  pushValue(method.paymentKey);
  pushValue(method.identifier);
  pushValue(method.type);
  pushValue(method.displayName);

  if (Array.isArray(method.names)) {
    method.names.forEach((entry) => {
      if (typeof entry === "string") {
        pushValue(entry);
      } else if (entry && typeof entry === "object") {
        pushValue(entry.name);
        pushValue(entry.value);
        pushValue(entry.label);
      }
    });
  }

  if (method.translations && typeof method.translations === "object") {
    Object.values(method.translations).forEach((value) => {
      if (typeof value === "string") {
        pushValue(value);
      } else if (value && typeof value === "object") {
        pushValue(value.name);
        pushValue(value.value);
        pushValue(value.label);
      }
    });
  }

  return values;
}

export default Vue.component("payment-provider-select", {
  data() {
    return {
      biozidActive: false,
      prepaymentMethod: null,
    };
  },

  props: {
    template: {
      type: String,
      default: "#vue-payment-provider-select",
    },
    paddingClasses: {
      type: String,
      default: null,
    },
    paddingInlineStyles: {
      type: String,
      default: null,
    },
  },

  computed: {
    ...mapState({
      storeMethodOfPaymentList: (state) => state.checkout.payment.methodOfPaymentList,
      methodOfPaymentId: (state) => state.checkout.payment.methodOfPaymentId,
      showError: (state) => state.checkout.validation.paymentProvider.showError,
      isBasketLoading: (state) => state.basket.isBasketLoading,
      isCheckoutReadonly: (state) => state.checkout.readOnly,
      selectedShippingProfile: (state) => state.checkout.shipping.selectedShippingProfile,
    }),

    methodOfPaymentList() {
      const list = Array.isArray(this.storeMethodOfPaymentList) ? this.storeMethodOfPaymentList : [];

      if (!this.biozidActive) {
        // Wenn kein Biozid aktiv: Filtere "Abgabegespräch" Zahlungsart aus der Liste
        return list.filter((method) => !this.isPrepaymentMethod(method));
      }

      // Wenn Biozid aktiv: Nur "Abgabegespräch" Zahlungsart anzeigen
      return this.prepaymentMethod ? [this.prepaymentMethod] : list;
    },
  },

  created() {
    this.$store.commit("setPaymentProviderValidator", this.validate);

    if (typeof document !== "undefined") {
      this.updateBiozidState();
      document.addEventListener("biozid-active-changed", this.updateBiozidState);
    }

    this.cachePrepaymentMethod(this.storeMethodOfPaymentList);

    if (this.biozidActive) {
      this.$nextTick(() => this.forcePrepaymentSelection());
    }
  },

  beforeDestroy() {
    if (typeof document !== "undefined") {
      document.removeEventListener("biozid-active-changed", this.updateBiozidState);
    }
  },

  watch: {
    storeMethodOfPaymentList: {
      immediate: true,
      handler(newList) {
        this.cachePrepaymentMethod(newList);

        if (this.biozidActive) {
          this.$nextTick(() => this.forcePrepaymentSelection());
        }
      },
    },
    biozidActive(newValue) {
      if (newValue) {
        this.$nextTick(() => this.forcePrepaymentSelection());
      } else {
        // Wenn Biozid deaktiviert wird: Wähle erste normale Zahlungsart oder Vorkasse
        this.$nextTick(() => this.selectFallbackPayment());
      }
    },
  },

  methods: {
    updateBiozidState() {
      if (typeof document === "undefined" || !document.body) {
        this.biozidActive = false;
        return;
      }

      this.biozidActive = document.body.classList.contains("biozid-active");
    },

    cachePrepaymentMethod(methodList) {
      if (Array.isArray(methodList)) {
        this.prepaymentMethod = methodList.find((method) => this.isPrepaymentMethod(method)) || null;
      } else {
        this.prepaymentMethod = null;
      }
    },

    isPrepaymentMethod(method) {
      const possibleValues = collectPossibleValues(method);
      // Zahlungsartennamen nachdem gesucht wird: "Abgabegespräch erforderlich"
      return possibleValues.some((value) => value.includes("abgabegespräch erforderlich") || value.includes("abgabegespräch"));
    },

    forcePrepaymentSelection() {
      if (!this.prepaymentMethod || !this.prepaymentMethod.id) {
        return;
      }

      if (Number(this.methodOfPaymentId) === Number(this.prepaymentMethod.id)) {
        this.validate();
        return;
      }

      this.onPaymentProviderChange(this.prepaymentMethod);
    },

    onPaymentProviderChange(newMethodOfPayment) {
      let targetMethod = newMethodOfPayment;

      if (this.biozidActive && this.prepaymentMethod && !this.isPrepaymentMethod(newMethodOfPayment)) {
        targetMethod = this.prepaymentMethod;
      }

      if (!targetMethod || !targetMethod.id) {
        return;
      }

      if (Number(this.methodOfPaymentId) === Number(targetMethod.id)) {
        this.validate();
        return;
      }

      this.$store.dispatch("selectMethodOfPayment", targetMethod.id).then(
        (data) => {
          document.dispatchEvent(new CustomEvent("afterPaymentMethodChanged", { detail: this.methodOfPaymentId }));
        },
        (error) => {
          console.log("error");
        }
      );

      this.validate();
    },

    selectFallbackPayment() {
      const list = Array.isArray(this.storeMethodOfPaymentList) ? this.storeMethodOfPaymentList : [];

      // Filtere Abgabegespräch aus der Liste
      const availableMethods = list.filter((method) => !this.isPrepaymentMethod(method));

      if (availableMethods.length === 0) {
        return;
      }

      // Suche nach Vorkasse/Prepayment
      const vorkasseMethod = availableMethods.find((method) => {
        const values = collectPossibleValues(method);
        return values.some((value) => value.includes("vorkasse") || value.includes("prepayment"));
      });

      // Wähle Vorkasse wenn vorhanden, sonst erste Zahlungsart
      const targetMethod = vorkasseMethod || availableMethods[0];

      if (!targetMethod || !targetMethod.id) {
        return;
      }

      // Nur wechseln wenn aktuell Abgabegespräch gewählt ist
      if (this.prepaymentMethod && Number(this.methodOfPaymentId) === Number(this.prepaymentMethod.id)) {
        this.onPaymentProviderChange(targetMethod);
      }
    },

    validate() {
      const showError = !(this.methodOfPaymentId > 0);

      this.$store.commit("setPaymentProviderShowError", showError);

      if (showError) {
        NotificationService.error(TranslationService.translate("Ceres::Template.checkoutCheckPaymentProvider"));
      }
    },

    isPaymentMethodExcluded(paymentMethodId) {
      if (isDefined(this.selectedShippingProfile?.excludedPaymentMethodIds)) {
        return this.selectedShippingProfile.excludedPaymentMethodIds.includes(paymentMethodId);
      }

      return false;
    },
  },
});
